import type { ElementProps, FloatingContext } from '@floating-ui/react'
import {
  autoUpdate,
  flip,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
} from '@floating-ui/react'
import { useId } from '@react-aria/utils'
import debounce from 'lodash.debounce'
import React from 'react'
import type { VirtuosoHandle, VirtuosoProps } from 'react-virtuoso'
import { ulid } from 'ulid'
import { useI18nContext } from '../_hooks/I18n'
import { useZIndexContext } from '../_hooks/ZIndex'
import { spacing } from '../_styles/spacing'
import * as defaultComponents from './SuperSelect.components'
import {
  draggableOptionIdSymbol,
  isOptSelectAllSymbol,
} from './SuperSelect.constants'
import { extendedSelectMenuWidth } from './SuperSelect.styles'
import type {
  KeyboardSelectionProps,
  SuperSelectApi,
  SuperSelectConfig,
  SuperSelectOption,
  SuperSelectResult,
  SuperSelectValue,
  SuperSelectValuePrimitive,
  TokenNavigationProps,
} from './SuperSelect.types'
import {
  collectGroupsInOrderOfOccurrence,
  createOptgroup,
  defaultOnUnselectAll,
  getBatchOptionFormatter,
  getIsAllOptionsUngrouped,
  getOptionIsOptgroup,
  getOptionIsOptSelectAll,
  getOptionsSortingAlgorithm,
  isMultiple,
  removeEmptyOptGroups,
  reorder,
  sortOptgroups,
} from './SuperSelect.utils'

const listContainerVerticalPadding = spacing.sm * 2
/* Based on item height + padding (~35px), this minimum height is enough to show 4-6 items before
 * the overflow is hidden before scrolling. This will ensure the dropdown doesn't open with minimal
 * height, which would lead to a poor scrolling experience. */
const reasonableDropdownMinimumHeight = 248

function noop() {}

function defaultGetOptionValue(option: SuperSelectOption) {
  return option?.value
}

function defaultGetOptionIsBatch(option: SuperSelectOption): boolean {
  return Array.isArray(option?.value)
}

function defaultGetOptionIsDisabled(option: SuperSelectOption): boolean {
  return option?.disabled ?? false
}

function defaultGetOptionGroup(option: SuperSelectOption): string {
  return option?.group ?? ''
}

function defaultSetOptionGroup(
  option: SuperSelectOption,
  group: string
): SuperSelectOption {
  return {
    ...option,
    group,
  }
}

function defaultGetOptionLabel(option: SuperSelectOption): string {
  return option?.label ?? ''
}

function stringContains(str1: string, str2: string) {
  return str1.toLowerCase().includes(str2.toLowerCase())
}

function useTokenNavigation(
  context: FloatingContext<HTMLElement>,
  { enabled = true, value = [], tokenRemoveRefs }: TokenNavigationProps = {}
): ElementProps {
  return {
    reference: {
      onKeyDown(e: React.KeyboardEvent) {
        if (!enabled || !Array.isArray(value) || value.length === 0) {
          return
        }

        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          tokenRemoveRefs?.current[value.length - 1]?.focus()
        }
      },
    },
  }
}

function useKeyboardSelection(
  context: FloatingContext<HTMLElement>,
  { enabled = true, onSelect = () => {} }: KeyboardSelectionProps = {}
): ElementProps {
  function onKeyDown(e: React.KeyboardEvent) {
    if (!enabled) {
      return
    }

    if (e.key === 'Enter') {
      onSelect()
    }
  }

  return {
    reference: {
      onKeyDown,
    },
    floating: {
      onKeyDown,
    },
  }
}

export function useSuperSelect({
  block = false,
  components: customComponents,
  defaultValue,
  disabled = false,
  draggable = false,
  emptyMessage = 'No results',
  selectAllEnabled = false,
  error,
  footer,
  getOptionGroup = defaultGetOptionGroup,
  getOptionIsBatch = defaultGetOptionIsBatch,
  getOptionIsDisabled = defaultGetOptionIsDisabled,
  getOptionLabel = defaultGetOptionLabel,
  getOptionIsPartiallySelected,
  getOptionIsSelected,
  getOptionValue = defaultGetOptionValue,
  onScrollBottom = noop,
  onOpenChange = noop,
  header,
  loading = false,
  multiple = false,
  onChange,
  onManualSort,
  onUnselectAll = defaultOnUnselectAll,
  onSelectAll,
  options: sourceOptions = [],
  placeholder,
  preset = '',
  presetProps,
  search = true,
  selectionStyle = 'highlight',
  setOptionGroup = defaultSetOptionGroup,
  sort = true,
  tabIndex = 0,
  value: value_,
  overlayMatchesTriggerWidth = true,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: SuperSelectConfig): SuperSelectApi {
  React.useEffect(() => {
    if (!draggable) {
      return
    }

    const isUsingDefaultGetter = getOptionGroup === defaultGetOptionGroup
    const isUsingDefaultSetter = setOptionGroup === defaultSetOptionGroup

    if (
      (isUsingDefaultGetter && !isUsingDefaultSetter) ||
      (!isUsingDefaultGetter && isUsingDefaultSetter)
    ) {
      console.warn(
        `SuperSelect: Using potentially conflicting "getOptionGroup" and "setOptionGroup" implementations.
        Group reassignment after drag-and-drop operation might be broken.`
      )
    }
  }, [draggable, getOptionGroup, setOptionGroup])

  const i18n = useI18nContext()

  const initialValue = defaultValue ?? (multiple ? [] : null)
  const [val, setVal] = React.useState<SuperSelectValue>(initialValue)
  const value = value_ !== undefined ? value_ : val

  /**
   * Tracks whether the "Select All" feature is currently active.
   * When true, new options that appear will be automatically selected.
   * This state is activated when all available options are selected and
   * deactivated when any option is individually deselected.
   */
  const [isSelectAllActive, setIsSelectAllActive] = React.useState(false)

  /**
   * Flag to prevent recursive auto-selection calls during state updates.
   * Ensures that auto-selection logic doesn't trigger multiple times
   * concurrently, which could cause performance issues or incorrect state.
   */
  const isProcessingAutoSelectionRef = React.useRef(false)

  /**
   * Stores a snapshot of previously selected values to compare against
   * current options for auto-selection.
   */
  const previousSelectedValuesRef = React.useRef<Array<SuperSelectValue>>([])

  function setValue(v: SuperSelectValue) {
    if (!value_) {
      setVal(v)
    }

    if (onChange) {
      onChange(v)
    }
  }

  /**
   * Extracts all primitive values from an option, handling both single values and batch values.
   * For batch options (arrays), returns all values flattened into a single array.
   * For single options, wraps the value in an array for consistent processing.
   *
   * @param option - The option to extract values from
   * @returns Array of primitive values contained in the option
   */
  const getOptionValuesFlat = React.useCallback(
    (option: SuperSelectOption): SuperSelectValuePrimitive[] => {
      const optionValue = getOptionValue(option)
      return Array.isArray(optionValue) ? optionValue : [optionValue]
    },
    [getOptionValue]
  )

  /**
   * Determines if all selectable options are currently selected.
   * Uses early termination to improve performance when checking if all selectable options are selected.
   *
   * Performance optimizations:
   * - Early exit if value count is less than options count
   * - Uses early termination in the validation loop
   *
   * @param currentValue - Current selected values array
   * @param currentSelectableOptions - Array of selectable options to check
   * @returns true if all selectable options are selected, false otherwise
   */
  const checkIfAllSelectableOptionsSelected = React.useCallback(
    (
      currentValue: SuperSelectValue,
      currentSelectableOptions: SuperSelectOption[]
    ) => {
      if (
        !Array.isArray(currentValue) ||
        currentSelectableOptions.length === 0
      ) {
        return false
      }

      const valueSet = new Set(currentValue)

      // Use early termination for better performance
      for (const option of currentSelectableOptions) {
        const optionValues = getOptionValuesFlat(option)
        if (!optionValues.every((val) => valueSet.has(val))) {
          return false
        }
      }

      return true
    },
    [getOptionValuesFlat]
  )

  /**
   * Performs automatic selection of newly added options when Select All is active.
   *
   * Algorithm:
   * 1. Compares current options with previous snapshot to find new options
   * 2. Automatically selects any newly detected options
   * 3. Implements safeguards against recursive calls
   *
   * Performance considerations:
   * - Single pass to build current options map and detect new ones
   * - Set-based deduplication for efficient value management
   *
   * @param currentValue - Current selected values
   */
  const executeAutoSelection = React.useCallback(
    (currentValue: SuperSelectValue) => {
      // Early exit conditions for performance
      if (
        !isSelectAllActive ||
        !multiple ||
        !Array.isArray(currentValue) ||
        isProcessingAutoSelectionRef.current
      ) {
        return
      }

      previousSelectedValuesRef.current =
        currentValue as Array<SuperSelectValue>

      const newlyAddedOptions: SuperSelectOption[] = []
      const currentValueSet = new Set(currentValue)

      for (const option of selectableOptions) {
        const optionValues = getOptionValuesFlat(option)

        const hasNewValue = optionValues.some(
          (val) => !currentValueSet.has(val)
        )

        if (hasNewValue) {
          newlyAddedOptions.push(option)
        }
      }

      if (newlyAddedOptions.length === 0) {
        return
      }

      isProcessingAutoSelectionRef.current = true

      const newValuesToAdd: SuperSelectValuePrimitive[] = []

      // Collect new values that aren't already selected
      for (const option of newlyAddedOptions) {
        const optionValues = getOptionValuesFlat(option)
        for (const val of optionValues) {
          if (!currentValueSet.has(val)) {
            newValuesToAdd.push(val)
          }
        }
      }

      // Only update if there are new values to add
      if (newValuesToAdd.length > 0) {
        // Use Set for efficient deduplication
        const combinedValueSet = new Set([...currentValue, ...newValuesToAdd])
        setValue(Array.from(combinedValueSet))
      }

      // Reset processing flag
      isProcessingAutoSelectionRef.current = false
    },
    [isSelectAllActive, multiple, setValue, getOptionValuesFlat]
  )

  // Debounced auto-selection for handling rapid option changes
  const debouncedAutoSelection = React.useMemo(
    () => debounce(executeAutoSelection, 50), // Optimized 50ms debounce
    [executeAutoSelection]
  )

  const navigationList = React.useRef<Array<HTMLElement | null>>([])
  const virtuoso = React.useRef<VirtuosoHandle>(null)
  const searchRef = React.useRef<HTMLButtonElement>(null)

  const overlayId = useId() // TODO use React 18 useId
  const listId = useId() // TODO use React 18 useId
  const selectedValueId = useId()
  const tokenListId = useId()

  const [open, setOpen] = React.useState(false)
  const [pointer, setPointer] = React.useState(false)
  const [width, setWidth] = React.useState(248)
  const [maxHeight, setMaxHeight] = React.useState(248)
  const [listHeight, setListHeight] = React.useState(0)
  const [searchHeight, setSearchHeight] = React.useState(0)
  const [footerHeight, setFooterHeight] = React.useState(0)
  const listContainerHeight = Math.min(
    maxHeight - searchHeight - footerHeight + listContainerVerticalPadding,
    listHeight + listContainerVerticalPadding
  )
  const [searchValue, setSearchValue_] = React.useState('')
  const setSearchValue = debounce(setSearchValue_, 250) // TODO use React 18 useDeferredValue

  const [activeMenuIndex, setActiveMenuIndex] = React.useState<number | null>(
    null
  )
  const tokenRemoveRefs = React.useRef<Array<HTMLButtonElement | null>>([])

  const components = {
    ...defaultComponents,
    ...(customComponents || {}),
  }

  // TODO #memogetters: consider having getOption... getter functions memoized by consumers
  // Until then, exclude these callbacks from effect and memo dependencies
  const sortOptions = React.useMemo(
    () => getOptionsSortingAlgorithm({ getOptionIsBatch, getOptionLabel }),
    // skip `getOptionIsBatch` and `getOptionLabel`
    []
  )

  const formatBatchOption = React.useMemo(
    () =>
      getBatchOptionFormatter({
        value,
        multiple,
        getOptionIsBatch,
        getOptionValue,
      }),
    // skip `getOptionIsBatch` and `getOptionValue`, refer to TODO #memogetters
    [value, multiple]
  )

  // collect groups, sort them and populate them with options, and sort the options inside the groups
  const enforceOptionsSortingOrder = React.useCallback(
    function groupAndSort(opts: SuperSelectOption[]) {
      const { groups, groupedOptions } = collectGroupsInOrderOfOccurrence(
        opts,
        getOptionGroup
      )

      if (getIsAllOptionsUngrouped(groups)) {
        return opts.sort(sortOptions)
      }

      return Object.entries(groupedOptions)
        .sort(([groupA], [groupB]) => sortOptgroups(groupA, groupB))
        .flatMap(([groupName, groupOptions]) =>
          createOptgroup(groupName, groupOptions.sort(sortOptions))
        )
    },
    // skip `getOptionGroup`, refer to TODO #memogetters
    [sortOptions]
  )

  // collect groups and populate them with options
  const deriveOptionsSortingOrder = React.useCallback(
    (opts: SuperSelectOption[]) => {
      const { groups, groupedOptions } = collectGroupsInOrderOfOccurrence(
        opts,
        getOptionGroup
      )

      if (getIsAllOptionsUngrouped(groups)) {
        return opts
      }

      // display optgroups in the order of occurrence, as they are considered pre-sorted
      return groups.flatMap((group) => {
        const groupOptions = groupedOptions[group]

        return createOptgroup(group, groupOptions)
      })
    },
    // skip `getOptionGroup`, refer to TODO #memogetters
    []
  )

  const groupAndSortAlgorithm = React.useMemo(
    () => (sort ? enforceOptionsSortingOrder : deriveOptionsSortingOrder),
    [sort, enforceOptionsSortingOrder, deriveOptionsSortingOrder]
  )

  const options = React.useMemo(
    () => {
      const queried = searchValue
        ? sourceOptions.filter((opt) =>
            stringContains(getOptionLabel(opt), searchValue)
          )
        : [...sourceOptions] // make a copy of source options to prevent the mutation when sorting

      return groupAndSortAlgorithm(queried).map(formatBatchOption)
    },
    // skip `getOptionLabel`, refer to TODO #memogetters
    [groupAndSortAlgorithm, formatBatchOption, searchValue, sourceOptions]
  )

  /**
   * Filtered list of options that can be selected (excludes disabled options and optgroups).
   * Used for auto-selection logic to determine which options should be automatically
   * selected when Select All is active and new options appear.
   */
  const selectableOptions = React.useMemo(() => {
    const result: SuperSelectOption[] = []
    for (const option of options) {
      if (isSelectableOption(option)) {
        result.push(option)
      }
    }
    return result
  }, [options])

  /**
   * Effect function that triggers auto-selection when conditions are met.
   * Calls the debounced auto-selection logic when:
   * - Select All is enabled
   * - Multiple selection mode is active
   * - Select All state is currently active
   * - Current value is an array (multiple selection)
   *
   * The debouncing helps handle rapid option changes efficiently.
   */
  const autoSelectionEffect = React.useCallback(() => {
    if (
      selectAllEnabled &&
      multiple &&
      isSelectAllActive &&
      Array.isArray(value) &&
      (value as SuperSelectValue[]).length
    ) {
      debouncedAutoSelection(value)
    }
  }, [
    selectAllEnabled,
    multiple,
    isSelectAllActive,
    value,
    debouncedAutoSelection,
  ])

  /**
   * Effect that manages auto-selection behavior and cleanup.
   * - Triggers auto-selection when dependencies change
   * - Runs on every render when auto-selection conditions might have changed
   */
  React.useEffect(() => {
    autoSelectionEffect()

    return () => {
      // Cleanup debounced function
      debouncedAutoSelection.cancel?.()

      // Reset processing flag
      isProcessingAutoSelectionRef.current = false
    }
  }, [autoSelectionEffect, debouncedAutoSelection])

  const selectAllOption = {
    label: i18n.t('core.select.selectAll'),
    id: 'selectAll',
    [isOptSelectAllSymbol]: true,
  }

  const allOptionsWithSelectAll = React.useMemo(() => {
    if (!selectAllEnabled || !multiple) {
      return options
    }
    return [selectAllOption, ...options]
  }, [selectAllEnabled, multiple, selectAllOption, options])

  const [draggableOptions, setDraggableOptions] = React.useState<
    SuperSelectOption[]
  >([])

  React.useEffect(
    () => {
      if (!draggable) {
        return
      }
      // make a copy of source options to prevent the mutation when sorting
      const opts = groupAndSortAlgorithm(
        sourceOptions.map((opt) => ({
          ...opt,
          [draggableOptionIdSymbol]: ulid(),
        }))
      )
      setDraggableOptions(opts)
    },
    // in draggable mode, sourceOptions must be memoized, othewise reorder will not work
    [draggable, sourceOptions, groupAndSortAlgorithm]
  )

  const queriedDraggableOptions = React.useMemo(
    () => {
      if (!draggable) {
        return []
      }

      const queried = searchValue
        ? removeEmptyOptGroups(
            draggableOptions.filter((opt) => {
              if (!getOptionIsOptgroup(opt)) {
                return stringContains(getOptionLabel(opt), searchValue)
              }
              return true
            })
          )
        : draggableOptions

      return queried.map(formatBatchOption)
    },
    // skip `getOptionLabel`, refer to TODO #memogetters
    [draggable, draggableOptions, searchValue, formatBatchOption]
  )

  // Group indices calculation - needs allOptionsWithSelectAll because:
  // When SelectAll is enabled, it becomes the first item (index 0) and shifts all other indices by 1.
  // Group headers need their correct absolute positions in the combined list for navigation to work.
  const groupIndices = React.useMemo(() => {
    const baseOptions =
      selectAllEnabled && multiple ? allOptionsWithSelectAll : options
    return baseOptions.reduce((acc, opt, i) => {
      if (getOptionIsOptgroup(opt)) {
        acc.push(i)
      }
      return acc
    }, [] as number[])
  }, [selectAllEnabled, multiple, allOptionsWithSelectAll, options])

  // Selected index calculation - needs allOptionsWithSelectAll because:
  // The selectedIndex must reference the absolute position in the combined list for keyboard navigation.
  // When SelectAll is at index 0, the first regular option becomes index 1, affecting selection highlighting.
  const selectedIndex = React.useMemo(() => {
    const baseOptions =
      selectAllEnabled && multiple ? allOptionsWithSelectAll : options
    return baseOptions.findIndex(
      (option) => isSelectableOption(option) && getOptionValue(option) === value
    )
  }, [selectAllEnabled, multiple, allOptionsWithSelectAll, options, value])

  // Selected option lookup - needs allOptionsWithSelectAll because:
  // The selectedOption lookup must search the complete list including SelectAll to find matches.
  // This ensures proper option detection regardless of whether SelectAll or regular options are selected.
  const selectedOption = React.useMemo(() => {
    const val = Array.isArray(value) ? value[0] : value
    const baseOptions =
      selectAllEnabled && multiple ? allOptionsWithSelectAll : options
    return baseOptions.find((option) => getOptionValue(option) === val)
  }, [selectAllEnabled, multiple, allOptionsWithSelectAll, options, value])

  const selectedLabel = React.useMemo(() => {
    return selectedOption ? getOptionLabel(selectedOption) : ''
  }, [selectedOption])

  function isSelectableOption(o: SuperSelectOption) {
    return !getOptionIsOptgroup(o) && !getOptionIsDisabled(o)
  }

  function getFirstSelectableOptionIndex() {
    // When Select All is enabled in multiple mode, it's always at index 0 and is selectable
    if (selectAllEnabled && multiple) {
      return 0
    }

    const baseOptions = options
    return (
      baseOptions.findIndex(isSelectableOption) +
      (selectAllEnabled && multiple ? 1 : 0)
    )
  }

  // Helper functions for onSelect optimization
  /**
   * Handles the toggle behavior for the Select All option.
   *
   * Behavior:
   * - If all options are selected: deselects all and deactivates Select All
   * - If not all options are selected: selects all and activates Select All
   *
   * Performance optimizations:
   * - Uses Set for efficient value deduplication
   * - Leverages existing option checking logic
   * - Calls optional onSelectAll callback for external handling
   */
  const handleSelectAllToggle = React.useCallback(() => {
    const getIsSelected = getOptionIsSelected ?? defaultGetOptionIsSelected
    const allSelected = selectableOptions.every((opt) => getIsSelected(opt))

    if (allSelected) {
      const newValue = onUnselectAll(
        value,
        options,
        searchValue,
        getOptionValuesFlat
      )
      setValue(newValue)
    } else {
      if (onSelectAll) {
        onSelectAll(selectableOptions)
      }
      const currentValue = Array.isArray(value) ? value : []
      const valueSet = new Set(currentValue)
      selectableOptions.forEach((opt) => {
        getOptionValuesFlat(opt).forEach((val) => valueSet.add(val))
      })
      setValue(Array.from(valueSet))
    }
  }, [
    getOptionIsSelected,
    selectableOptions,
    onSelectAll,
    setIsSelectAllActive,
    setValue,
    value,
    getOptionValuesFlat,
  ])

  /**
   * Determines if Select All should be activated based on the new value.
   * Select All activates when all selectable options become selected,
   * but only if it wasn't already active (prevents unnecessary state changes).
   *
   * @param currentSelectableOptions - Current array of selectable options to check against
   * @returns true if Select All should be activated, false otherwise
   */
  const shouldActivateSelectAll = React.useCallback(
    (currentSelectableOptions: SuperSelectOption[]) => {
      return (
        selectAllEnabled &&
        !searchValue &&
        checkIfAllSelectableOptionsSelected(value, currentSelectableOptions)
      )
    },
    [selectAllEnabled, searchValue, value, checkIfAllSelectableOptionsSelected]
  )

  // Centralize select all active state
  React.useEffect(() => {
    const shouldBeActive =
      shouldActivateSelectAll(selectableOptions) &&
      selectableOptions.length &&
      !!(value as Array<SuperSelectValue>).length

    // When Select All is already active and new selectable options are added,
    // skip updating the Select All active state here. This allows the existing
    // auto-selection logic to handle newly added options without triggering
    // unnecessary state changes in this effect.
    const hasNewOptions =
      selectableOptions.length > previousSelectedValuesRef.current.length
    const hasRemovedValue =
      ((value as Array<SuperSelectValue>) || []).length <
      previousSelectedValuesRef.current.length

    if (
      isSelectAllActive &&
      hasNewOptions &&
      !hasRemovedValue &&
      (value as Array<SuperSelectValue>).length
    ) {
      return
    }

    if (shouldBeActive) {
      setIsSelectAllActive(true)
    } else {
      setIsSelectAllActive(false)
    }
  }, [isSelectAllActive, selectableOptions, shouldActivateSelectAll, value])

  /**
   * Handles selection of batch options (options with multiple values).
   * Adds all values from the batch option to the current selection
   * and activates Select All if all options become selected.
   *
   * @param optionValue - Array of primitive values from the batch option
   */
  const handleBatchOptionSelection = React.useCallback(
    (optionValue: SuperSelectValuePrimitive[]) => {
      // Ensure we're working with an array for multiple selection
      const currentValue = Array.isArray(value) ? value : []
      const newValueSet = new Set([...currentValue, ...optionValue])
      const newValue = Array.from(newValueSet)
      setValue(newValue)
    },
    [value, setValue]
  )

  /**
   * Handles selection/deselection of individual options in multiple mode.
   *
   * Behavior:
   * - If option is selected: removes it and deactivates Select All
   * - If option is not selected: adds it and activates Select All if all options become selected
   *
   * @param optionValue - The primitive value of the option to toggle
   */
  const handleSingleOptionSelection = React.useCallback(
    (optionValue: SuperSelectValuePrimitive) => {
      // Ensure we're working with an array for multiple selection
      if (!Array.isArray(value)) {
        return
      }

      const valueIndex = value.indexOf(optionValue)

      if (valueIndex >= 0) {
        // Remove value
        const newValue = value.filter((_, i) => i !== valueIndex)
        setValue(newValue)
      } else {
        // Add value
        const newValue = [...value, optionValue]
        setValue(newValue)
      }
    },
    [value, setValue]
  )

  const onSelect = React.useCallback(
    (option: SuperSelectOption) => {
      if (getOptionIsDisabled(option)) {
        return
      }

      const optionValue = getOptionValue(option)
      const isSelectAll = getOptionIsOptSelectAll(option)

      if (isMultiple(multiple, value)) {
        if (isSelectAll) {
          handleSelectAllToggle()
        } else if (Array.isArray(optionValue)) {
          handleBatchOptionSelection(optionValue)
        } else {
          handleSingleOptionSelection(optionValue)
        }
      } else {
        setValue(optionValue)
      }
    },
    [
      getOptionIsDisabled,
      getOptionValue,
      getOptionIsOptSelectAll,
      multiple,
      value,
      handleSelectAllToggle,
      handleBatchOptionSelection,
      handleSingleOptionSelection,
      setValue,
    ]
  )

  function onKeyboardSelect() {
    if (activeMenuIndex !== null) {
      // Select All must be included in the list for keyboard navigation to work properly.
      // The activeMenuIndex corresponds to positions in the complete navigation list including SelectAll.
      const baseOptions =
        selectAllEnabled && multiple ? allOptionsWithSelectAll : options
      const option = baseOptions[activeMenuIndex]

      if (option) {
        onSelect(option)
      }
    }
  }

  function defaultGetOptionIsSelected(option: SuperSelectOption) {
    if (Array.isArray(value)) {
      const optionValues = getOptionValue(option)

      if (Array.isArray(optionValues)) {
        return optionValues.every((v) => value.includes(v))
      }

      return value.includes(optionValues)
    }

    return value === getOptionValue(option)
  }

  function defaultGetOptionIsPartiallySelected(option: SuperSelectOption) {
    if (selectionStyle !== 'checkbox') {
      return false
    }

    if (Array.isArray(value)) {
      const optionValues = getOptionValue(option)

      if (Array.isArray(optionValues)) {
        return (
          optionValues.some((v) => value.includes(v)) &&
          !optionValues.every((v) => value.includes(v))
        )
      }
    }

    return false
  }

  function defaultGetOptionIsSelectAllPartiallySelected(
    options: SuperSelectOption[],
    isSelected: (option: SuperSelectOption) => boolean,
    isPartiallySelected: (option: SuperSelectOption) => boolean
  ) {
    if (selectionStyle !== 'checkbox' && !Array.isArray(value)) {
      return false
    }

    return (
      options.some(
        (option) => isSelected(option) || isPartiallySelected(option)
      ) && !options.every((option) => isSelected(option))
    )
  }

  function defaultGetOptionIsSelectAllSelected(
    options: SuperSelectOption[],
    isSelected: (option: SuperSelectOption) => boolean
  ) {
    if (!multiple) {
      return false
    }

    return options.every((option) => isSelected(option))
  }

  function isEmpty() {
    return Array.isArray(value) ? value.length === 0 : value === null
  }

  const boundWidthRef = React.useRef(open)

  React.useEffect(() => {
    boundWidthRef.current = open
  }, [open])

  const floating = useFloating<HTMLElement>({
    open,
    onOpenChange: (nextOpen) => {
      setOpen(nextOpen)
      onOpenChange(nextOpen)

      if (!nextOpen && multiple) {
        requestAnimationFrame(() => {
          searchRef.current?.focus()
        })
      }
    },
    whileElementsMounted: (...args) =>
      /* { animationFrame: true } is used to fix ResizeObserver issue with @floating-ui/react */
      autoUpdate(...args, { animationFrame: true }),
    strategy: 'fixed',
    middleware: [
      offset(({ rects }) => {
        return {
          mainAxis: 2,
          crossAxis: overlayMatchesTriggerWidth
            ? 0
            : (rects.floating.width - rects.reference.width) / 2,
        }
      }),
      flip(),
      size({
        apply({ availableHeight, elements }) {
          if (boundWidthRef.current) {
            boundWidthRef.current = false
            setWidth(
              overlayMatchesTriggerWidth
                ? elements.reference.getBoundingClientRect().width
                : extendedSelectMenuWidth
            )
          }
          if (availableHeight >= reasonableDropdownMinimumHeight) {
            setMaxHeight(availableHeight)
          }
        },
        padding: 10,
      }),
    ],
  })

  const { value: zIndex } = useZIndexContext()

  const activeDescendantId = React.useMemo(() => {
    if (activeMenuIndex === null) return undefined
    const baseOptions =
      selectAllEnabled && multiple ? allOptionsWithSelectAll : options
    const option = baseOptions[activeMenuIndex]
    if (!option) return undefined
    if (getOptionIsOptSelectAll(option)) return `item-${selectAllOption.id}`
    return `item-${getOptionValue(option)}`
  }, [
    activeMenuIndex,
    selectAllEnabled,
    multiple,
    allOptionsWithSelectAll,
    options,
    selectAllOption,
  ])

  function getAriaLabelProps() {
    return {
      ...(ariaLabel && { 'aria-label': ariaLabel }),
      ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
    }
  }

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      useClick(floating.context, {
        enabled: !disabled,
        keyboardHandlers: false,
      }),
      useDismiss(floating.context, {
        enabled: !disabled,
      }),
      useListNavigation(floating.context, {
        activeIndex: activeMenuIndex,
        disabledIndices: groupIndices,
        enabled: !disabled,
        listRef: navigationList,
        loop: true,
        onNavigate: (i) => setActiveMenuIndex(() => i),
        selectedIndex,
        virtual: true,
      }),
      useTokenNavigation(floating.context, {
        enabled: !disabled && multiple && searchValue === '',
        tokenRemoveRefs,
        value,
      }),
      useKeyboardSelection(floating.context, {
        enabled: !disabled,
        onSelect: onKeyboardSelect,
      }),
      {
        reference: {
          $block: block,
          $disabled: disabled,
          $error: error,
          $hasClearIcon: !disabled && !loading,
          $loading: loading,
          $multiple: multiple,
          $open: open,
          $placeholder: !selectedLabel,
          'aria-activedescendant': multiple ? undefined : activeDescendantId,
          ...(!multiple && {
            'aria-controls': open ? overlayId : undefined,
            'aria-describedby': selectedValueId,
            'aria-expanded': open,
            'aria-haspopup': 'listbox',
            ...getAriaLabelProps(),
            role: 'button',
            tabIndex: disabled ? -1 : tabIndex,
          }),
          onKeyDown() {
            setPointer(false)
          },
          search,
        } as any, // TODO fix type
        floating: {
          role: search ? 'listbox' : 'none',
          id: overlayId,
          onKeyDown() {
            setPointer(false)
          },
          onPointerMove() {
            setPointer(true)
          },
          style: {
            position: floating.strategy,
            left: floating.x || 0,
            top: floating.y || 0,
            width,
            maxHeight,
            zIndex,
          },
        },
      },
    ]
  )

  function getLabelProps() {
    return {
      $hoverable: false,
      id: selectedValueId,
    }
  }

  function getMultiInputProps(): React.AriaAttributes &
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
      ref: React.Ref<HTMLButtonElement>
      placeholder?: string
    } {
    const showPlaceholder = isEmpty()

    return {
      'aria-activedescendant': activeDescendantId,
      'aria-controls': open ? overlayId : undefined,
      'aria-expanded': open,
      'aria-haspopup': 'listbox' as const,
      ...getAriaLabelProps(),
      role: 'combobox',
      ref: searchRef,
      tabIndex: disabled ? -1 : tabIndex,
      placeholder: showPlaceholder ? placeholder : '',
      disabled,
      onKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Tab') {
          if (open) {
            e.preventDefault()
            e.stopPropagation()
            floating.refs.floating.current?.focus()
          }
        }
      },
    }
  }

  function getMultiValueProps(index: number) {
    function removeToken() {
      if (!isMultiple(multiple, value)) return

      const nextVal = value.filter((_, i) => i !== index)
      setValue(nextVal)

      requestAnimationFrame(() => {
        if (nextVal.length === 0) {
          searchRef.current?.focus()
        } else if (index >= nextVal.length) {
          tokenRemoveRefs.current[nextVal.length - 1]?.focus()
        } else {
          tokenRemoveRefs.current[index]?.focus()
        }
      })
    }

    return {
      ref(el: HTMLButtonElement | null) {
        tokenRemoveRefs.current[index] = el
      },
      onClick(e: React.MouseEvent) {
        e.stopPropagation()
        removeToken()
      },
      onKeyDown(e: React.KeyboardEvent) {
        if (e.key === 'Enter' || e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault()
          e.stopPropagation()
          removeToken()
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault()
          e.stopPropagation()
          if (index > 0) {
            tokenRemoveRefs.current[index - 1]?.focus()
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          e.stopPropagation()
          if (isMultiple(multiple, value) && index < value.length - 1) {
            tokenRemoveRefs.current[index + 1]?.focus()
          } else {
            searchRef.current?.focus()
          }
        }
      },
    }
  }

  function getSearchContainerProps() {
    return {
      ref: (el: HTMLElement | null) => {
        setSearchHeight(el?.clientHeight ?? 0)
      },
    }
  }

  function getSearchProps() {
    return {
      'aria-activedescendant': activeDescendantId,
      'aria-controls': listId,
      'aria-expanded': open,
      'aria-haspopup': 'listbox' as const,
      onChange(value: string) {
        setSearchValue(value)
      },
      placeholder: i18n.t('core.select.search'),
      role: 'combobox',
    }
  }

  function getHeaderProps() {
    return {
      ref: (el: HTMLElement | null) => {},
    }
  }

  function getFooterProps() {
    return {
      ref: (el: HTMLElement | null) => {
        setFooterHeight(el?.clientHeight ?? 0)
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key !== 'Escape') {
          e.stopPropagation()
        }
      },
    }
  }

  function getEmptyMessageProps() {
    return {
      emptyMessage,
    }
  }

  function getClearProps() {
    return {
      'aria-hidden': true,
      'aria-label': i18n.t('core.select.clear'),
      tabIndex: -1,
      onClick(e: React.MouseEvent) {
        // prevent the menu from closing
        e.stopPropagation()

        setValue(multiple ? [] : null)
        setOpen(true)
      },
    }
  }

  function getMenuProps() {
    return {
      scrollable: false,
      style: {
        height: listContainerHeight,
      },
    }
  }

  function getVirtuosoProps(): VirtuosoProps<{}, {}> {
    const atBottomStateChange = (atBottom: boolean) => {
      if (atBottom) {
        onScrollBottom()
      }
    }

    return {
      role: 'listbox',
      id: listId,
      data: draggable ? queriedDraggableOptions : options,
      components: {
        Item: components?.Item as any, // TODO fix type
      },
      initialTopMostItemIndex: selectedIndex >= 0 ? selectedIndex : 0,
      style: {
        flex: '1 1 auto',
      },
      tabIndex: -1,
      totalListHeightChanged: setListHeight,
      atBottomStateChange,
    }
  }

  const onDragEnd = React.useCallback((result: SuperSelectResult) => {
    if (!result.destination) {
      return
    }
    if (result.source.index === result.destination.index) {
      return
    }

    const startIndex = result.source.index
    const destinationIndex = result.destination.index

    setDraggableOptions((options) => {
      const { nextOptions, prevGroup, nextGroup } = reorder(
        options,
        startIndex,
        destinationIndex
      )

      const adjustedOptions =
        prevGroup !== nextGroup
          ? nextOptions.map((option, index) => {
              if (index === destinationIndex) {
                return setOptionGroup(option, nextGroup)
              }
              return option
            })
          : nextOptions

      if (typeof onManualSort === 'function') {
        setTimeout(() => {
          onManualSort(
            adjustedOptions.filter((option) => !getOptionIsOptgroup(option))
          )
        }, 0)
      }

      return adjustedOptions
    })
  }, [])

  // run effect when the `open` state changes
  //
  // if it was closed and is now open, reset the highlighted index to be
  // the first selectable option
  //
  // if it was open and is now closed, reset the search value and focused token index
  React.useEffect(() => {
    if (open) {
      setActiveMenuIndex(() =>
        selectedIndex >= 0 ? selectedIndex : getFirstSelectableOptionIndex()
      )
    } else {
      setSearchValue(() => '')
    }
  }, [open])

  // run the effect when user enters a serach value
  //
  // if the select is currently open then reset highlighted item to be the first
  // selectable option
  React.useEffect(() => {
    if (open) {
      setActiveMenuIndex(() => getFirstSelectableOptionIndex())
    }
  }, [searchValue])

  // when we change the value then close the overlay, but only if we have
  // actually selected a value
  //
  // multiple mode forces the overlay to stay open
  React.useEffect(() => {
    if (!multiple && value !== null) {
      setOpen(() => false)
    }
  }, [value])

  // recreate the array to hold dom refs when our options change
  React.useEffect(() => {
    // Adjust length to include Select All option if enabled, without this the navigation
    // would not work correctly when Select All is enabled
    const totalLength =
      selectAllEnabled && multiple
        ? allOptionsWithSelectAll.length
        : options.length
    navigationList.current = [...Array(totalLength).fill(null)]
  }, [selectAllEnabled, multiple, allOptionsWithSelectAll, options])

  // scroll virtuoso list activeMenuIndex into view when the index changes
  React.useLayoutEffect(() => {
    if (activeMenuIndex !== null) {
      if (!pointer) {
        // Adjust index for virtuoso if Select All is enabled (since Select All is rendered separately)
        // Without this the navigation would not work correctly when Select All is enabled
        const virtuosoIndex =
          selectAllEnabled && multiple && activeMenuIndex > 0
            ? activeMenuIndex + 1
            : activeMenuIndex

        if (virtuosoIndex >= 0) {
          virtuoso.current?.scrollIntoView({ index: virtuosoIndex })
        }
      }
    }
  }, [activeMenuIndex, open, selectAllEnabled, multiple])

  return {
    config: {
      block,
      disabled,
      draggable,
      emptyMessage,
      error,
      footer,
      header,
      placeholder,
      preset,
      presetProps,
      selectionStyle,
      tabIndex,
      loading,
      multiple,
      selectAllEnabled,
    },
    components,
    state: {
      activeMenuIndex,
      isEmpty,
      isSelectAllActive,
      listContainerHeight,
      listId,
      maxHeight,
      tokenListId,
      onDragEnd,
      onSelect,
      onSelectAll,
      open,
      options: draggable ? queriedDraggableOptions : options,
      overlayId,
      searchValue,
      selectAllOption,
      selectedIndex,
      selectedLabel,
      selectedOption,
      setOpen,
      setPointer,
      setSearchValue: setSearchValue,
      setActiveMenuIndex,
      sourceOptions,
      value,
      width,
    },
    props: {
      clear: getClearProps,
      emptyMessage: getEmptyMessageProps,
      footer: getFooterProps,
      header: getHeaderProps,
      item: getItemProps,
      label: getLabelProps,
      menu: getMenuProps,
      multiInput: getMultiInputProps,
      overlay: getFloatingProps,
      search: getSearchProps,
      searchContainer: getSearchContainerProps,
      tokenClear: getMultiValueProps,
      trigger: getReferenceProps,
      virtuoso: getVirtuosoProps,
    },
    option: {
      group: getOptionGroup,
      isBatch: getOptionIsBatch,
      isPartiallySelected:
        getOptionIsPartiallySelected ?? defaultGetOptionIsPartiallySelected,
      isSelected: getOptionIsSelected ?? defaultGetOptionIsSelected,
      isDisabled: getOptionIsDisabled,
      isOptgroup: getOptionIsOptgroup,
      isOptSelectAll: getOptionIsOptSelectAll,
      label: getOptionLabel,
      value: getOptionValue,
      selectAll: {
        isSelectAllPartiallySelected: (options) =>
          defaultGetOptionIsSelectAllPartiallySelected(
            options,
            getOptionIsSelected ?? defaultGetOptionIsSelected,
            getOptionIsPartiallySelected ?? defaultGetOptionIsPartiallySelected
          ),
        isSelectAllSelected: (options) =>
          defaultGetOptionIsSelectAllSelected(
            options,
            getOptionIsSelected ?? defaultGetOptionIsSelected
          ),
      },
    },
    refs: {
      floating,
      navigationList,
      virtuoso,
    },
  }
}

export const SuperSelectContext = React.createContext<SuperSelectApi | null>(
  null
)

export function useSuperSelectContext(): SuperSelectApi {
  const ctx = React.useContext(SuperSelectContext)

  if (!ctx) {
    throw new Error('Invalid SuperSelectContext')
  }

  return ctx as SuperSelectApi
}
